import { Asset, AssetUtils } from "../newTypes/Asset";
import {
  DetailedPolicy,
  DetailedPolicyUtils,
} from "../newTypes/DetailedSafeObjects/DetailedPolicy";
import { DSOFactory } from "../newTypes/DetailedSafeObjects/DSOFactory";
import { DetailedSafeObjectUtils } from "../newTypes/DetailedSafeObjects/IDetailedSafeObjecet";
import { ILightGroup, LightGroupUtils } from "./lightGroup";

export interface ILightSnapshot {
  address: string;
  name: string;
  groups: ILightGroup[];
  assets: Asset[];
  allPolicies: DetailedPolicy[];
}

// Helper function: nestedGroupsFromMap
function nestedGroupsFromMap(groupsMap: Record<string, any>[]): ILightGroup[] {
  try {
    const resultsAsMap: Record<string, any>[] = [];

    const findGroupMap = ({
      groupID,
      groupsList,
    }: {
      groupID: string;
      groupsList: Record<string, any>[];
    }): Record<string, any> | null => {
      const founded = groupsList.find((g) => g["id"] === groupID) || {};
      return Object.keys(founded).length === 0 ? null : founded;
    };

    const aggregateGroupOwner = (group: Record<string, any>): Record<string, any> => {
      const groupMap = { ...group };
      const groupMapObj = { ...group["object"] };
      const groupOwner: string[] = [];
      const parent = findGroupMap({ groupID: groupMapObj["parentGroup"], groupsList: groupsMap });
      if (parent) {
        groupOwner.push(groupMapObj["parentGroup"]);
        const updatedParent =
          findGroupMap({ groupID: groupMapObj["parentGroup"], groupsList: resultsAsMap }) ??
          aggregateGroupOwner(parent);
        const updatedParentObj = updatedParent["object"];
        groupOwner.push(...updatedParentObj["groupOwner"]);
      }
      groupMapObj["groupOwner"] = groupOwner;
      groupMap["object"] = groupMapObj;
      return groupMap;
    };

    return (
      groupsMap.map((groupMap) => {
        const updated = aggregateGroupOwner(groupMap);
        resultsAsMap.push(updated);
        return LightGroupUtils.fromMap(updated);
      }) as ILightGroup[]
    ).filter((g) => g.isValid);
  } catch (e) {
    console.error(String(e));
    console.error(e); // Stack trace equivalent
    return [];
  }
}

export const LightSnapshotUtils = {
  create({
    address = "",
    name = "",
    groups = [],
    assets = [],
    allPolicies = [],
  }: {
    address?: string;
    name?: string;
    groups?: ILightGroup[];
    assets?: Asset[];
    allPolicies?: DetailedPolicy[];
  }): ILightSnapshot {
    return {
      address,
      name,
      groups,
      assets,
      allPolicies,
    };
  },

  empty(): ILightSnapshot {
    return LightSnapshotUtils.create({
      address: "",
      name: "",
      groups: [],
      assets: [],
      allPolicies: [],
    });
  },

  fromMap(map: Record<string, any>): ILightSnapshot {
    const groups =
      map["groups"] && map["groups"] !== ""
        ? nestedGroupsFromMap((map["groups"] as any[]).map((g) => g as Record<string, any>))
        : [];

    const allPolicies = map["groups"]
      .map((g) => g.nestedPolicies)
      .reduce((acc, curr) => acc.concat(curr), [])
      .map((p) => DSOFactory.policyFromMap(p));

    const assetsFromMap =
      map["assets"] && Array.isArray(map["assets"]["assets"])
        ? (map["assets"]["assets"] as any[])
            .map((x) => AssetUtils.fromMap(x))
            .filter((x) => x.symbol && x.symbol !== "" && x.symbol !== "Error")
        : [];
    try {
      return LightSnapshotUtils.create({
        address: map.address || "",
        name: map.name || "",
        groups: groups,
        assets: assetsFromMap,
        allPolicies: allPolicies,
      });
    } catch (e) {
      console.error("Error creating ILeanSnapshot from map:", e);
      return LightSnapshotUtils.empty();
    }
  },

  fromJson(source: string): ILightSnapshot {
    try {
      return LightSnapshotUtils.fromMap(JSON.parse(source));
    } catch (e) {
      console.error("Error parsing ILeanSnapshot from JSON:", e);
      return LightSnapshotUtils.empty();
    }
  },

  toMap(snapshot: ILightSnapshot): Record<string, any> {
    return {
      address: snapshot.address,
      name: snapshot.name,
      groups: snapshot.groups,
      assets: snapshot.assets,
      allPolicies: snapshot.allPolicies,
    };
  },

  toJson(snapshot: ILightSnapshot): string {
    return JSON.stringify(LightSnapshotUtils.toMap(snapshot));
  },

  copyWith(
    snapshot: ILightSnapshot,
    {
      address,
      name,
      groups,
      assets,
      allPolicies,
    }: {
      address?: string;
      name?: string;
      groups?: ILightGroup[];
      assets?: Asset[];
      allPolicies?: DetailedPolicy[];
    }
  ): ILightSnapshot {
    return LightSnapshotUtils.create({
      address: address ?? snapshot.address,
      name: name ?? snapshot.name,
      groups: groups ?? snapshot.groups,
      assets: assets ?? snapshot.assets,
      allPolicies: allPolicies ?? snapshot.allPolicies,
    });
  },
};
